import { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { ORDER_TABLES } from '../../lib/dispatchOrders';
import { changeOrderTable } from '../../lib/orderTableService';

export default function ChangeTableModal({ order, orders, currentUser, onClose, onPrint }) {
  const dialogRef = useRef(null);
  const savingRef = useRef(false);
  const [table, setTable] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const occupied = orders.filter((other) => other.id !== order.id && other.table === table).length;

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);

  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || saved) return;
    savingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await changeOrderTable({ user: currentUser, orderId: order.id, expectedTable: order.table || '', nextTable: table });
      setSaved(true);
    } catch (err) {
      setError(err.code === 'permission-denied'
        ? 'No se pudo guardar el cambio. Solicita a administración que revise tu acceso.'
        : err.message || 'No se pudo cambiar la mesa. Intenta nuevamente.');
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };

  const reprint = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setBusy(true);
    setError('');
    try {
      const snapshot = await getDoc(doc(db, 'orders', order.id));
      if (!snapshot.exists()) throw new Error('El pedido ya no existe.');
      await onPrint({ ...snapshot.data(), id: order.id });
      onClose();
    } catch (err) {
      setError('La mesa quedó guardada, pero no se pudo reimprimir. Puedes reintentar desde el pedido.');
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <dialog ref={dialogRef} aria-labelledby="change-table-title"
      onCancel={(event) => { event.preventDefault(); if (!savingRef.current) onClose(); }}
      className="w-[calc(100%-2rem)] max-w-md rounded-3xl p-6 shadow-2xl backdrop:bg-slate-900/50">
      <h2 id="change-table-title" className="text-2xl font-black text-slate-900">{saved ? 'Mesa actualizada' : 'Cambiar mesa'}</h2>
      <p className="mt-2 break-words text-sm text-slate-600">Pedido #{order.id}</p>
      {saved ? (
        <div className="mt-4">
          <p role="status" className="rounded-xl bg-emerald-50 p-3 font-semibold text-emerald-900">Cambio guardado: {order.table || 'Sin identificar'} → {table}.</p>
          <p className="mt-3 text-sm text-slate-600">La comanda anterior conserva la mesa original. Puedes reimprimirla con la ubicación actual.</p>
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-xl bg-slate-100 px-4 py-3 font-semibold disabled:opacity-50">Cerrar</button>
            <button type="button" disabled={busy} onClick={reprint} className="rounded-xl bg-sky-700 px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? 'Reimprimiendo…' : 'Reimprimir comanda'}</button>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="mt-4">
          <p className="mb-4 text-slate-700">Mesa actual: <strong>{order.table || 'Sin identificar'}</strong></p>
          <label htmlFor="destination-table" className="font-semibold text-slate-800">Nueva mesa</label>
          <select id="destination-table" autoFocus required disabled={busy} value={table} onChange={(event) => setTable(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-3 text-base">
            <option value="">Seleccionar mesa…</option>
            {ORDER_TABLES.filter((value) => value !== order.table).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          {occupied > 0 && <p role="status" className="mt-3 rounded-xl bg-amber-100 p-3 text-sm text-amber-950">{table} ya tiene {occupied} pedido(s). Las cuentas se mantendrán separadas.</p>}
          {table && <p className="mt-4 font-semibold text-slate-800">Mover este pedido de {order.table || 'Sin identificar'} a {table}.</p>}
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-xl bg-slate-100 px-4 py-3 font-semibold disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={busy || !table} className="rounded-xl bg-sky-700 px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? 'Guardando…' : 'Confirmar cambio'}</button>
          </div>
        </form>
      )}
      {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    </dialog>
  );
}
